import type { Doc, Id } from "@Aspecto/backend/convex/_generated/dataModel";

import { api } from "@Aspecto/backend/convex/_generated/api";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  Bell,
  Calendar,
  CheckCheck,
  MessageSquare,
  FileText,
  Newspaper,
  User,
  UserCheck,
  UserPlus,
  UserX,
  Users,
} from "lucide-react";
import { useState } from "react";

import { AppPageContent } from "@/components/layout/app-page";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { PageHeader } from "@/components/ui/page-header";
import { SectionCard } from "@/components/ui/section-card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/app/notifications/")({
  component: NotificationsPage,
});

function NotificationsPage() {
  const { data: notifications } = useSuspenseQuery(
    convexQuery(api.notifications.getUserNotifications, {}),
  );
  const [activeTab, setActiveTab] = useState<"all" | "unread">("all");

  const { mutate: markAllAsRead, isPending: isMarkingAll } = useMutation({
    mutationFn: useConvexMutation(api.notifications.markAllAsRead),
  });

  const filteredNotifications =
    activeTab === "unread" ? notifications.filter((n) => !n.isRead) : notifications;

  const unreadCount = notifications.filter((n) => !n.isRead).length;

  return (
    <AppPageContent>
      <PageHeader
        title="Notifications"
        description={
          unreadCount > 0
            ? `You have ${unreadCount} unread notification${unreadCount !== 1 ? "s" : ""}`
            : "You're all caught up"
        }
        action={
          unreadCount > 0 ? (
            <Button variant="outline" onClick={() => markAllAsRead({})} disabled={isMarkingAll}>
              <CheckCheck className="mr-2 h-4 w-4" />
              Mark all as read
            </Button>
          ) : undefined
        }
      />

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as "all" | "unread")}>
        <TabsList className="mb-6">
          <TabsTrigger value="all">All</TabsTrigger>
          <TabsTrigger value="unread">
            Unread
            {unreadCount > 0 && (
              <span className="ml-1.5 rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-medium text-primary-foreground">
                {unreadCount}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="all">
          <NotificationList notifications={filteredNotifications} />
        </TabsContent>

        <TabsContent value="unread">
          <NotificationList notifications={filteredNotifications} />
        </TabsContent>
      </Tabs>
    </AppPageContent>
  );
}

type NotificationWithDetails = Doc<"notifications"> & {
  actor: {
    _id: Id<"users">;
    firstName?: string;
    lastName?: string;
    imageUrl?: string;
  } | null;
  groupName: string | null;
};

function NotificationList({ notifications }: { notifications: NotificationWithDetails[] }) {
  const navigate = useNavigate();
  const { mutate: markAsRead } = useMutation({
    mutationFn: useConvexMutation(api.notifications.markAsRead),
  });

  const handleNotificationClick = (notification: NotificationWithDetails) => {
    if (!notification.isRead) {
      markAsRead({ notificationId: notification._id });
    }

    if (notification.type === "digest_sent" && notification.newsletterId) {
      navigate({
        to: "/app/newsletters/$newsletterId",
        params: { newsletterId: notification.newsletterId },
      });
      return;
    }

    if (notification.groupId) {
      const subRoute =
        notification.type === "event_created"
          ? "/app/groups/$groupId/events"
          : notification.type === "post_created" || notification.type === "comment_reply"
            ? "/app/groups/$groupId/posts"
            : notification.type === "join_request"
              ? "/app/groups/$groupId/members"
              : notification.type === "join_request_approved"
                ? "/app/groups/$groupId"
                : "/app/groups/$groupId";

      navigate({
        to: subRoute,
        params: { groupId: notification.groupId },
      });
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case "event_created":
        return Calendar;
      case "post_created":
        return FileText;
      case "comment_reply":
        return MessageSquare;
      case "digest_sent":
        return Newspaper;
      case "group_invitation":
        return Users;
      case "join_request":
        return UserPlus;
      case "join_request_approved":
        return UserCheck;
      case "join_request_rejected":
        return UserX;
      default:
        return Bell;
    }
  };

  const formatRelativeTime = (timestamp: number) => {
    const now = Date.now();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / (60 * 1000));
    const hours = Math.floor(diff / (60 * 60 * 1000));
    const days = Math.floor(diff / (24 * 60 * 60 * 1000));

    if (minutes < 1) return "Just now";
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return new Date(timestamp).toLocaleDateString();
  };

  if (notifications.length === 0) {
    return (
      <EmptyState
        icon={Bell}
        title="No notifications"
        description="When you receive notifications, they'll appear here."
      />
    );
  }

  return (
    <SectionCard noPadding>
      <ul className="divide-y">
        {notifications.map((notification) => {
          const Icon = getNotificationIcon(notification.type);

          return (
            <li
              key={notification._id}
              onClick={() => handleNotificationClick(notification)}
              className={cn(
                "flex cursor-pointer items-start gap-4 p-4 transition-all duration-200 hover:bg-muted/50",
                !notification.isRead && "bg-primary/5",
              )}
            >
              <div className="relative">
                {notification.actor?.imageUrl ? (
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={notification.actor.imageUrl} />
                    <AvatarFallback>{notification.actor.firstName?.[0] ?? "U"}</AvatarFallback>
                  </Avatar>
                ) : (
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted">
                    <User className="h-5 w-5 text-muted-foreground" />
                  </div>
                )}
                <div className="absolute -bottom-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-background">
                  <Icon className="h-3 w-3 text-muted-foreground" />
                </div>
              </div>

              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-2">
                  <p className={cn("text-sm", !notification.isRead && "font-medium")}>
                    {notification.title}
                  </p>
                  <span className="whitespace-nowrap text-xs text-muted-foreground">
                    {formatRelativeTime(notification.createdAt)}
                  </span>
                </div>
                {notification.type === "digest_sent" ? (
                  <div className="mt-1 space-y-1.5">
                    {notification.message && (
                      <p className="text-sm text-muted-foreground">{notification.message}</p>
                    )}
                    {notification.groupName && (
                      <span className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                        Published in
                        <span className="font-medium text-foreground">
                          {notification.groupName}
                        </span>
                      </span>
                    )}
                  </div>
                ) : (
                  <p className="mt-0.5 text-sm text-muted-foreground">{notification.message}</p>
                )}
              </div>

              {!notification.isRead && <div className="mt-2 h-2 w-2 rounded-full bg-primary" />}
            </li>
          );
        })}
      </ul>
    </SectionCard>
  );
}
